import * as XLSX from 'xlsx';

export interface ExportColumn<T> {
  key: keyof T | string;
  header: string;
  format?: (value: unknown, row: T) => string | number;
}

export const formatDateForExport = (date: string | Date): string => {
  if (!date) {
    return '';
  }
  const d = new Date(date);
  return d.toLocaleDateString();
};

export const formatBooleanForExport = (value: boolean): string => (value ? 'Yes' : 'No');

function prepareRowsForExport<T>(
  data: T[],
  columns?: ExportColumn<T>[]
): Record<string, string | number>[] {
  if (!columns || columns.length === 0) {
    return data as Record<string, string | number>[];
  }

  return data.map((row) => {
    const formattedRow: Record<string, string | number> = {};

    columns.forEach((column) => {
      const value =
        typeof column.key === 'string' ? (row as Record<string, unknown>)[column.key] : row[column.key as keyof T];
      const formattedValue = column.format ? column.format(value, row) : value;
      if (typeof formattedValue === 'number' || typeof formattedValue === 'string') {
        formattedRow[column.header] = formattedValue;
      } else if (formattedValue === null || formattedValue === undefined) {
        formattedRow[column.header] = '';
      } else {
        formattedRow[column.header] = String(formattedValue);
      }
    });

    return formattedRow;
  });
}

export const exportToCSV = <T>(data: T[], columns: ExportColumn<T>[], fileName: string) => {
  const rows = prepareRowsForExport(data, columns);
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const exportToExcel = <T>(data: T[], columns: ExportColumn<T>[], fileName: string) => {
  const rows = prepareRowsForExport(data, columns);
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
